import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Upload, Download, Loader2, Truck, CheckCircle2 } from 'lucide-react';

const enconOrange = '#ee7203';

const TIER_LABELS = {
  exact: { label: 'Exact', color: 'bg-emerald-100 text-emerald-700' },
  high: { label: 'High Confidence', color: 'bg-amber-100 text-amber-700' },
  needs_review: { label: 'Needs Review', color: 'bg-red-100 text-red-700' },
  manual: { label: 'Manually Confirmed', color: 'bg-blue-100 text-blue-700' },
  unmapped_at_source: { label: 'Unmapped at Source', color: 'bg-gray-200 text-gray-600' },
};

const PhocasSupplierProfile = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tierFilter, setTierFilter] = useState('needs_review');
  const [searchQuery, setSearchQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [vendorQueryBySupplier, setVendorQueryBySupplier] = useState({});
  const [vendorOptionsBySupplier, setVendorOptionsBySupplier] = useState({});
  const [confirmingCode, setConfirmingCode] = useState(null);
  const fileInputRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get(`${API}/master-supplier-profile/stats`);
      setStats(res.data);
    } catch (e) {
      toast.error('Failed to load supplier profile stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const res = await api.get(`${API}/master-supplier-profile`, { params: { tier: tierFilter, q: searchQuery, limit: 200 } });
      setRows(res.data.results || []);
    } catch (e) {
      toast.error('Failed to load supplier profile rows');
    } finally {
      setRowsLoading(false);
    }
  }, [tierFilter, searchQuery]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadRows, 300);
    return () => clearTimeout(t);
  }, [loadRows]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Please upload a .json file (same structure as master-supplier-stock-profile.json)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`${API}/master-supplier-profile/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const s = res.data.stats || {};
      toast.success(`Imported: ${s.exact || 0} exact, ${s.high || 0} high, ${s.needs_review || 0} needs review, ${s.unchanged_confirmed || 0} unchanged (confirmed, untouched)`);
      loadStats();
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to import supplier profile');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`${API}/master-supplier-profile/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master-supplier-stock-profile-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export supplier profile');
    } finally {
      setDownloading(false);
    }
  };

  const searchVendors = async (phocasCode, q) => {
    setVendorQueryBySupplier(prev => ({ ...prev, [phocasCode]: q }));
    if (!q || q.trim().length < 2) return;
    try {
      const res = await api.get(`${API}/master-supplier-profile/vendor-search`, { params: { q } });
      setVendorOptionsBySupplier(prev => ({ ...prev, [phocasCode]: res.data.suppliers || [] }));
    } catch (e) { /* non-fatal */ }
  };

  const confirmVendor = async (phocasCode, vendor) => {
    setConfirmingCode(phocasCode);
    try {
      await api.put(`${API}/master-supplier-profile/${encodeURIComponent(phocasCode)}/confirm`, {
        crm_vendor_id: vendor.id,
        crm_vendor_name: vendor.name,
      });
      toast.success(`${phocasCode} confirmed as ${vendor.name}`);
      loadStats();
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm supplier');
    } finally {
      setConfirmingCode(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="phocas-supplier-profile-page">
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Truck className="w-6 h-6" style={{ color: enconOrange }} />
              Phocas/CRM Supplier Data
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownload} disabled={downloading} data-testid="supplier-profile-download-btn">
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ backgroundColor: enconOrange }}
                className="text-white"
                data-testid="supplier-profile-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Phocas Export
              </Button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileUpload} className="hidden" data-testid="supplier-profile-file-input" />
            </div>
          </CardTitle>
          <CardDescription>
            Maps Phocas supplier stock codes to real Zoho CRM Vendors, product-code by product-code —
            used by Auto-Add-to-CRM to attach the correct supplier to an auto-added quote. Re-uploading
            re-matches against live CRM data without resetting anything already confirmed and unchanged.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-2">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold" style={{ color: enconOrange }}>{(stats?.total || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Suppliers</p>
              </div>
              {Object.entries(TIER_LABELS).map(([tier, cfg]) => (
                <button
                  key={tier}
                  onClick={() => setTierFilter(tier)}
                  className={`p-4 rounded-lg text-center transition-colors ${tierFilter === tier ? 'ring-2 ring-offset-1' : ''}`}
                  style={{ backgroundColor: '#f9fafb', ringColor: enconOrange }}
                  data-testid={`supplier-profile-tier-tile-${tier}`}
                >
                  <p className="text-2xl font-bold text-gray-700">{(stats?.by_tier?.[tier] || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{cfg.label}</p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-base">Browse &amp; Confirm — {TIER_LABELS[tierFilter]?.label}</CardTitle>
          <CardDescription>
            {tierFilter === 'exact' || tierFilter === 'manual'
              ? 'Already confirmed — usable by Auto-Add-to-CRM.'
              : 'Pending a human decision. Search the real Zoho Vendor and confirm — this is the SAME vendor search used elsewhere in Add to CRM, never a freeform name.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Search by Phocas code or supplier name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
              data-testid="supplier-profile-search-input"
            />
          </div>
          {rowsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400" data-testid="supplier-profile-rows-empty">No suppliers in this tier.</p>
          ) : (
            <div className="space-y-3 max-h-[32rem] overflow-y-auto">
              {rows.map(r => (
                <div key={r.phocas_supplier_code} className="rounded-xl border border-gray-200 p-3" data-testid={`supplier-profile-row-${r.phocas_supplier_code}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-mono text-xs text-gray-400">{r.phocas_supplier_code}</span>{' '}
                      <span className="font-semibold text-gray-800">{r.phocas_supplier_name}</span>{' '}
                      <Badge className={TIER_LABELS[r.match_tier]?.color}>{TIER_LABELS[r.match_tier]?.label || r.match_tier}</Badge>
                      <span className="text-xs text-gray-400 ml-2">{(r.product_codes || []).length} product code(s)</span>
                    </div>
                    {r.match_tier === 'exact' || r.match_tier === 'manual' ? (
                      <span className="text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> {r.crm_supplier_name}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {r.crm_supplier_name_suggested && (
                          <span className="text-xs text-gray-400">Suggested: {r.crm_supplier_name_suggested}</span>
                        )}
                        <Input
                          placeholder="Search Zoho vendors..."
                          value={vendorQueryBySupplier[r.phocas_supplier_code] ?? r.crm_supplier_name_suggested ?? ''}
                          onChange={(e) => searchVendors(r.phocas_supplier_code, e.target.value)}
                          className="w-48 h-8 text-sm"
                          data-testid={`supplier-profile-vendor-search-${r.phocas_supplier_code}`}
                        />
                        {(vendorOptionsBySupplier[r.phocas_supplier_code] || []).length > 0 && (
                          <select
                            className="border rounded px-2 py-1 text-sm"
                            onChange={(e) => {
                              const vendor = (vendorOptionsBySupplier[r.phocas_supplier_code] || []).find(v => v.id === e.target.value);
                              if (vendor) confirmVendor(r.phocas_supplier_code, vendor);
                            }}
                            data-testid={`supplier-profile-vendor-select-${r.phocas_supplier_code}`}
                            defaultValue=""
                          >
                            <option value="" disabled>Pick vendor…</option>
                            {(vendorOptionsBySupplier[r.phocas_supplier_code] || []).map(v => (
                              <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                          </select>
                        )}
                        {confirmingCode === r.phocas_supplier_code && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PhocasSupplierProfile;
